import React, { useMemo, useState } from 'react'
import {
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  ComposedChart,
  Bar,
} from 'recharts'
import { useEnergyStore } from '@/stores/useEnergyStore'
import { generateMonthlyData } from '@/engine/generateMonthlyData'
import { useSimulationData } from '@/hooks/useSimulationData'
import { Activity, Zap, Info, Maximize2, Minimize2 } from 'lucide-react'
import { LOCATION_DATA } from '@/data/locationData'
import { getSeasonalFactors } from '@/engine/seasonalFactors'
import { BASE_MATRIX_DATA } from '@/data/planPresets'

export const DashboardCharts: React.FC<{ hideHeader?: boolean }> = ({ hideHeader }) => {
  const config = useEnergyStore((s) => s.config)
  const plans = useEnergyStore((s) => s.plans)
  const portfolios = useEnergyStore((s) => s.portfolios)
  const activePortfolioId = useEnergyStore((s) => s.activePortfolioId)
  const activeHouseholdId = useEnergyStore((s) => s.activeHouseholdId)
  const currentUserId = useEnergyStore((s) => s.userId)
  const selectedPlanName = useEnergyStore((s) => s.selectedPlan)
  const selectedConfigId = useEnergyStore((s) => s.selectedConfig)
  const darkMode = useEnergyStore((s) => s.darkMode)
  const openHelpToItem = useEnergyStore((s) => s.openHelpToItem)
  const setDailyFlowsModalOpen = useEnergyStore((s) => s.setDailyFlowsModalOpen)

  const [isDailyView, setIsDailyView] = useState(false)
  const [activeIndexCost, setActiveIndexCost] = useState<number | null>(null)
  const [activeIndexFlat, setActiveIndexFlat] = useState<number | null>(null)
  const [activeIndexPerf, setActiveIndexPerf] = useState<number | null>(null)
  const [isMobile, setIsMobile] = useState(false)

  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const selectedPlan = useMemo(() => {
    const filteredPlans = plans.filter(p => {
      if (p.userId && p.userId !== currentUserId && (!p.isShared || p.householdId !== activeHouseholdId)) {
        return false
      }
      if (activePortfolioId !== 'all') {
        const portfolio = portfolios.find(port => port.id === activePortfolioId)
        if (portfolio && !portfolio.planIds.includes(p.id)) {
          return false
        }
      }
      return true
    })

    return filteredPlans.find((p) => p.plan === selectedPlanName) || filteredPlans[0] || plans[0] || BASE_MATRIX_DATA[0]!
  }, [plans, portfolios, activePortfolioId, currentUserId, activeHouseholdId, selectedPlanName])

  const { data: simulationMonthlyData } = useSimulationData()

  const { chartData, batteryProfileData, stats } = useMemo(() => {
    const data = generateMonthlyData(
      selectedPlan,
      selectedConfigId,
      config,
      simulationMonthlyData.length > 0 ? simulationMonthlyData : []
    ).map((d) => ({
      ...d,
      solarGenerationDaily: d.solarGenerationKwh / 30.4,
      totalLoadDaily: d.totalLoadKwh / 30.4,
    }))

    const batteryData = config.useEss 
      ? generateMonthlyData(selectedPlan, 'solar_bat', config, simulationMonthlyData.length > 0 ? simulationMonthlyData : [])
      : []

    // Calculate Annual KPIs using legacy simplified chart logic (12 months)
    let annualGenTotal = 0
    let annualConsTotal = 0
    let annualSelfTotal = 0

    const locData = LOCATION_DATA[config.location]
    const locMult = config.location === 'Custom' ? (config.customLocationMultiplier ?? 1.0) : (locData ? locData.multiplier : 1.0)

    for (let i = 0; i < 12; i++) {
      const { loadFactor, solarFactor } = getSeasonalFactors(i)
      const simMult = 1 + ((simulationMonthlyData.length > 0 ? (simulationMonthlyData[i % 12] ?? 0) : 0) / 100)
      const psh = 4.0 * solarFactor
      
      const dailyGen = config.pvSize * psh * (config.pvGeneration / 100) * (1 - config.systemLosses / 100) * locMult * simMult
      
      let inputToDaily = config.consumption || 1200
      if (config.consMode === 'annual') inputToDaily /= 365.25
      else if (config.consMode === 'monthly') inputToDaily /= 30.4
      else if (config.consMode === 'weekly') inputToDaily /= 7
      
      const dailyCons = inputToDaily * loadFactor
      
      annualGenTotal += dailyGen * 30.4
      annualConsTotal += dailyCons * 30.4
      
      const selfConsRate = config.useEss ? 0.65 : 0.35
      const dailySelf = Math.min(dailyCons, dailyGen * selfConsRate)
      annualSelfTotal += dailySelf * 30.4
    }

    return {
      chartData: data,
      batteryProfileData: batteryData,
      stats: {
        energyFromSolar: Math.round((annualGenTotal / (annualConsTotal || 1)) * 100),
        selfConsumption: Math.round((annualSelfTotal / (annualGenTotal || 1)) * 100),
        exportToGrid: Math.round(((annualGenTotal - annualSelfTotal) / (annualGenTotal || 1)) * 100)
      }
    }
  }, [selectedPlan, selectedConfigId, config, simulationMonthlyData])

  const batteryChartData = useMemo(() => {
    return batteryProfileData.map(d => {
      const autonomyPct = Math.max(0, ((730 - d.flatHours) / 730) * 100)
      return {
        ...d,
        autonomousHours: Math.max(0, 730 - d.flatHours),
        gridReliantHours: d.flatHours,
        autonomyPercentage: autonomyPct
      }
    })
  }, [batteryProfileData])

  return (
    <div className="space-y-8">
      {/* Section Header */}
      {!hideHeader && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${darkMode ? 'bg-white/5 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>
              <Zap size={18} />
            </div>
            <h2 className="text-sm font-black uppercase tracking-widest">Simulated performance analysis</h2>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-8">
        {/* Monthly Cost Comparison Chart */}
        <div className={`w-full relative transition-all duration-300 p-4 sm:p-6 rounded-2xl border ${darkMode ? 'bg-slate-900/40 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="flex items-start justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-1 mb-1">
                <button
                  onClick={() => openHelpToItem('Monthly Cost Chart')}
                  className={`shrink-0 p-0.5 rounded-full transition-colors cursor-pointer ${
                    darkMode ? 'text-slate-600 hover:text-emerald-500' : 'text-slate-400 hover:text-emerald-600'
                  }`}
                >
                  <Info size={10} />
                </button>
                <h3 className={`text-[10px] font-black uppercase tracking-[0.2em] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  Monthly Cost Comparison
                </h3>
              </div>
              <p className={`text-[10px] font-bold ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                Estimated monthly retail cost projections across scenarios
              </p>
            </div>
          </div>
          {/* Active Month Inspector */}
          <div className={`p-2.5 mb-2 rounded-xl text-center border text-[11px] font-bold min-h-[38px] flex items-center justify-center sm:hidden ${
            darkMode ? 'bg-slate-900/60 border-white/5 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-700'
          }`}>
            {activeIndexCost !== null && chartData[activeIndexCost] ? (
              (() => {
                const activePoint = chartData[activeIndexCost]
                if (!activePoint) return null
                return (
                  <div className="flex flex-wrap items-center justify-center gap-y-1.5 gap-x-4">
                    <span className={`text-[12px] font-black ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {activePoint.month}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#cbd5e1] dark:bg-[#334155]" />
                      Raw: <span className={`font-black ${darkMode ? 'text-white' : 'text-slate-900'}`}>${Math.round(activePoint.costWithoutSolar).toLocaleString()}</span>
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#10b981]" />
                      Net: <span className={`font-black ${darkMode ? 'text-white' : 'text-slate-900'}`}>${Math.round(activePoint.netCost).toLocaleString()}</span>
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#f59e0b]" />
                      Benefit: <span className={`font-black ${darkMode ? 'text-white' : 'text-slate-900'}`}>${Math.round(activePoint.solarBenefit).toLocaleString()}</span>
                    </span>
                  </div>
                )
              })()
            ) : (
              <span className={`text-[10px] opacity-60 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                💡 Tap or drag on the chart to inspect monthly details
              </span>
            )}
          </div>

          <div className={`w-full relative transition-all duration-500 touch-none h-[320px] sm:h-[400px]`}>
            {isMobile && (
              <div 
                className="absolute inset-0 z-10"
                onPointerMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const percentage = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                  setActiveIndexCost(Math.round(percentage * (chartData.length - 1)));
                }}
                onPointerDown={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const percentage = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                  setActiveIndexCost(Math.round(percentage * (chartData.length - 1)));
                }}
              />
            )}
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart 
                data={chartData} 
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                onMouseMove={(state) => {
                  if (!isMobile && state && typeof state.activeTooltipIndex === 'number') {
                    setActiveIndexCost(state.activeTooltipIndex)
                  }
                }}
                onMouseLeave={() => !isMobile && setActiveIndexCost(null)}
              >
                <defs>
                  <linearGradient id="colorRaw" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={darkMode ? '#334155' : '#cbd5e1'} stopOpacity={0.2}/>
                    <stop offset="95%" stopColor={darkMode ? '#334155' : '#cbd5e1'} stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorNet" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorBenefit" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke={darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}
                />
                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fontWeight: 700, fill: darkMode ? '#64748b' : '#94a3b8' }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fontWeight: 700, fill: darkMode ? '#64748b' : '#94a3b8' }}
                  tickFormatter={(val) => `$${val}`}
                />
                <Tooltip
                  content={<CustomTooltip darkMode={darkMode} isMobile={isMobile} />}
                  wrapperStyle={isMobile ? { display: 'none', pointerEvents: 'none' } : undefined}
                  cursor={isMobile ? false : true}
                />
                <Legend
                  verticalAlign="top"
                  align="right"
                  iconType="circle"
                  wrapperStyle={{ paddingBottom: '30px', fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}
                />
                <Area
                  name="Raw"
                  type="monotone"
                  dataKey="costWithoutSolar"
                  stroke={darkMode ? '#334155' : '#cbd5e1'}
                  fillOpacity={1}
                  fill="url(#colorRaw)"
                  strokeWidth={2}
                  isAnimationActive={false}
                  activeDot={{ r: 4, strokeWidth: 0, style: { pointerEvents: 'none' } }}
                  dot={(props: any) => <circle key={`raw-${props.index}`} cx={props.cx} cy={props.cy} r={isMobile && props.index === activeIndexCost ? 4 : 0} fill={darkMode ? '#334155' : '#cbd5e1'} style={{ opacity: isMobile && props.index === activeIndexCost ? 1 : 0 }} />}
                />
                <Area
                  name="Net"
                  type="monotone"
                  dataKey="netCost"
                  stroke="#10b981"
                  fillOpacity={1}
                  fill="url(#colorNet)"
                  strokeWidth={3}
                  isAnimationActive={false}
                  activeDot={{ r: 4, strokeWidth: 0, style: { pointerEvents: 'none' } }}
                  dot={(props: any) => <circle key={`net-${props.index}`} cx={props.cx} cy={props.cy} r={isMobile && props.index === activeIndexCost ? 4 : 0} fill="#10b981" style={{ opacity: isMobile && props.index === activeIndexCost ? 1 : 0 }} />}
                />
                <Area
                  name="Benefit"
                  type="monotone"
                  dataKey="solarBenefit"
                  stroke="#f59e0b"
                  fillOpacity={1}
                  fill="url(#colorBenefit)"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  isAnimationActive={false}
                  activeDot={{ r: 4, strokeWidth: 0, style: { pointerEvents: 'none' } }}
                  dot={(props: any) => <circle key={`ben-${props.index}`} cx={props.cx} cy={props.cy} r={isMobile && props.index === activeIndexCost ? 4 : 0} fill="#f59e0b" style={{ opacity: isMobile && props.index === activeIndexCost ? 1 : 0 }} />}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* System Autonomy Chart - Only shown for Solar + Battery */}
        {config.useEss && (
          <div className={`w-full relative transition-all duration-300 p-4 sm:p-6 rounded-2xl border ${darkMode ? 'bg-slate-900/40 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
            <div className="flex items-start justify-between gap-4 mb-8">
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <button
                    onClick={() => openHelpToItem('System Autonomy vs Grid Reliance')}
                    className={`shrink-0 p-0.5 rounded-full transition-colors cursor-pointer ${
                      darkMode ? 'text-slate-600 hover:text-emerald-500' : 'text-slate-400 hover:text-emerald-600'
                    }`}
                  >
                    <Info size={10} />
                  </button>
                  <h3 className={`text-[10px] font-black uppercase tracking-[0.2em] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    System Autonomy vs Grid Reliance
                  </h3>
                </div>
                <p className={`text-[10px] font-bold ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  Average hours per day your system operates independently of the grid
                </p>
              </div>
            </div>
            {/* Active Month Inspector */}
            <div className={`p-2.5 mb-2 rounded-xl text-center border text-[11px] font-bold min-h-[38px] flex items-center justify-center sm:hidden ${
              darkMode ? 'bg-slate-900/60 border-white/5 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-700'
            }`}>
              {activeIndexFlat !== null && batteryChartData[activeIndexFlat] ? (
                (() => {
                  const activePoint = batteryChartData[activeIndexFlat]
                  if (!activePoint) return null
                  return (
                    <div className="flex flex-wrap items-center justify-center gap-y-1.5 gap-x-4">
                      <span className={`text-[12px] font-black ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {activePoint.month}
                      </span>
                      <span className="inline-flex items-center gap-1.5 shrink-0">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        Auto: <span className={`font-black ${darkMode ? 'text-white' : 'text-slate-900'}`}>{Math.round(activePoint.autonomousHours)}h</span>
                      </span>
                      <span className="inline-flex items-center gap-1.5 shrink-0">
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                        Grid: <span className={`font-black ${darkMode ? 'text-white' : 'text-slate-900'}`}>{Math.round(activePoint.gridReliantHours)}h</span>
                      </span>
                    </div>
                  )
                })()
              ) : (
                <span className={`text-[10px] opacity-60 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  💡 Tap or drag on the chart to inspect monthly details
                </span>
              )}
            </div>

            <div className={`w-full relative transition-all duration-500 touch-none h-[320px] sm:h-[400px]`}>
              {isMobile && (
                <div 
                  className="absolute inset-0 z-10"
                  onPointerMove={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const percentage = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                    setActiveIndexFlat(Math.round(percentage * (batteryChartData.length - 1)));
                  }}
                  onPointerDown={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const percentage = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                    setActiveIndexFlat(Math.round(percentage * (batteryChartData.length - 1)));
                  }}
                />
              )}
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart 
                  data={batteryChartData} 
                  margin={{ top: 20, right: 10, left: -20, bottom: 0 }}
                  onMouseMove={(state) => {
                    if (!isMobile && state && typeof state.activeTooltipIndex === 'number') {
                      setActiveIndexFlat(state.activeTooltipIndex)
                    }
                  }}
                  onMouseLeave={() => !isMobile && setActiveIndexFlat(null)}
                >
                  <defs>
                    <linearGradient id="colorAuto" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorGrid" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#f43f5e" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke={darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}
                  />
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fontWeight: 700, fill: darkMode ? '#64748b' : '#94a3b8' }}
                    dy={10}
                  />
                  <YAxis
                    yAxisId="left"
                    axisLine={false}
                    tickLine={false}
                    domain={[0, 100]}
                    tick={{ fontSize: 10, fontWeight: 700, fill: darkMode ? '#64748b' : '#94a3b8' }}
                    tickFormatter={(val) => `${val}%`}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fontWeight: 700, fill: darkMode ? '#64748b' : '#94a3b8' }}
                    tickFormatter={(val) => `${val}h`}
                  />
                  <Tooltip
                    content={<CustomTooltip darkMode={darkMode} isMobile={isMobile} />}
                    wrapperStyle={isMobile ? { display: 'none', pointerEvents: 'none' } : undefined}
                    cursor={isMobile ? false : { fill: darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}
                  />
                  <Legend
                    verticalAlign="top"
                    align="right"
                    iconType="circle"
                    wrapperStyle={{ paddingBottom: '30px', fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}
                  />
                  <Area
                    yAxisId="right"
                    name="Grid Reliance"
                    type="monotone"
                    dataKey="gridReliantHours"
                    stroke="#f43f5e"
                    fillOpacity={1}
                    fill="url(#colorGrid)"
                    strokeWidth={2}
                    isAnimationActive={false}
                    activeDot={{ r: 4, strokeWidth: 0, style: { pointerEvents: 'none' } }}
                    dot={(props: any) => <circle key={`grid-${props.index}`} cx={props.cx} cy={props.cy} r={isMobile && props.index === activeIndexFlat ? 4 : 0} fill="#f43f5e" style={{ opacity: isMobile && props.index === activeIndexFlat ? 1 : 0 }} />}
                  />
                  <Area
                    yAxisId="left"
                    name="Autonomy"
                    type="monotone"
                    dataKey="autonomyPercentage"
                    stroke="#10b981"
                    fillOpacity={1}
                    fill="url(#colorAuto)"
                    strokeWidth={3}
                    isAnimationActive={false}
                    activeDot={{ r: 4, strokeWidth: 0, style: { pointerEvents: 'none' } }}
                    dot={(props: any) => <circle key={`auto-${props.index}`} cx={props.cx} cy={props.cy} r={isMobile && props.index === activeIndexFlat ? 4 : 0} fill="#10b981" style={{ opacity: isMobile && props.index === activeIndexFlat ? 1 : 0 }} />}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* System Performance Analysis */}
        <div className="space-y-6">
          <div className={`w-full relative transition-all duration-300 p-4 sm:p-6 rounded-2xl border ${darkMode ? 'bg-slate-900/40 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
              <div className="flex-1 w-full">
                <div className="flex items-start justify-between w-full gap-4">
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <button
                        onClick={() => openHelpToItem('Annual Profile')}
                        className={`shrink-0 p-0.5 rounded-full transition-colors cursor-pointer ${
                          darkMode ? 'text-slate-600 hover:text-emerald-500' : 'text-slate-400 hover:text-emerald-600'
                        }`}
                      >
                        <Info size={10} />
                      </button>
                      <h3 className={`text-[10px] font-black uppercase tracking-[0.2em] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        System Performance
                      </h3>
                    </div>
                    <p className={`text-[10px] font-bold ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      Annual solar generation vs consumption profile
                    </p>
                  </div>
                  
                  <div className="flex items-start gap-4 shrink-0">
                    {/* Mobile KPI */}
                    <div className="flex flex-col items-end sm:hidden shrink-0">
                      <div className={`text-2xl font-black tracking-tighter leading-none ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {stats.energyFromSolar}<span className="text-[10px] text-emerald-500 opacity-60 ml-0.5">%</span>
                      </div>
                      <div className="flex items-center justify-end gap-1 text-[7px] font-black uppercase tracking-widest text-emerald-500 mt-1">
                        <button
                          onClick={() => openHelpToItem('Energy from Solar')}
                          className="shrink-0 p-0.5 rounded-full hover:text-emerald-400 transition-colors cursor-pointer"
                        >
                          <Info size={8} />
                        </button>
                        Energy From Solar
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between sm:justify-start gap-3 mt-4 w-full">
                  <div className={`inline-flex items-center p-0.5 rounded-lg shrink-0 ${darkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                    <button
                      onClick={() => setIsDailyView(false)}
                      className={`px-3 py-1 rounded-md text-[10px] font-black uppercase tracking-wider transition-all ${
                        !isDailyView 
                          ? (darkMode ? 'bg-slate-700 text-white shadow-sm' : 'bg-white text-slate-800 shadow-sm')
                          : (darkMode ? 'text-slate-500 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700')
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => setIsDailyView(true)}
                      className={`px-3 py-1 rounded-md text-[10px] font-black uppercase tracking-wider transition-all ${
                        isDailyView 
                          ? (darkMode ? 'bg-slate-700 text-white shadow-sm' : 'bg-white text-slate-800 shadow-sm')
                          : (darkMode ? 'text-slate-500 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700')
                      }`}
                    >
                      Daily
                    </button>
                  </div>
                  <button
                    onClick={() => setDailyFlowsModalOpen(true)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all border shadow-sm ${
                      darkMode ? 'bg-slate-800/50 border-white/10 text-slate-300 hover:text-white hover:bg-slate-700' : 'bg-white border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                  >
                    <Activity size={12} />
                    Flows
                  </button>
                </div>
              </div>
              
              {/* Desktop KPI */}
              <div className="hidden sm:block text-right shrink-0">
                <div className={`text-3xl font-black tracking-tighter ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  {stats.energyFromSolar}<span className="text-sm text-emerald-500 opacity-60 ml-0.5">%</span>
                </div>
                <div className="flex items-center justify-end gap-1.5 text-[8px] font-black uppercase tracking-widest text-emerald-500">
                  <button
                    onClick={() => openHelpToItem('Energy from Solar')}
                    className="shrink-0 p-0.5 rounded-full hover:text-emerald-400 transition-colors cursor-pointer"
                  >
                    <Info size={10} />
                  </button>
                  Energy From Solar
                </div>
              </div>
            </div>

            {/* Active Month/Day Inspector */}
            <div className={`p-2.5 mb-2 rounded-xl text-center border text-[11px] font-bold min-h-[38px] flex items-center justify-center sm:hidden ${
              darkMode ? 'bg-slate-900/60 border-white/5 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-700'
            }`}>
              {activeIndexPerf !== null && chartData[activeIndexPerf] ? (
                (() => {
                  const activePoint = chartData[activeIndexPerf]
                  if (!activePoint) return null
                  return (
                    <div className="flex flex-wrap items-center justify-center gap-y-1.5 gap-x-4">
                      <span className={`text-[12px] font-black ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {activePoint.month}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#f59e0b]" />
                        Generation: <span className={`font-black ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                          {Math.round(isDailyView ? activePoint.solarGenerationDaily : activePoint.solarGenerationKwh).toLocaleString()} {isDailyView ? 'kWh/day' : 'kWh'}
                        </span>
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#6366f1]" />
                        Consumption: <span className={`font-black ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                          {Math.round(isDailyView ? activePoint.totalLoadDaily : activePoint.totalLoadKwh).toLocaleString()} {isDailyView ? 'kWh/day' : 'kWh'}
                        </span>
                      </span>
                    </div>
                  )
                })()
              ) : (
                <span className={`text-[10px] opacity-60 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  💡 Tap or drag on the chart to inspect {isDailyView ? 'daily' : 'monthly'} details
                </span>
              )}
            </div>

            <div className={`w-full relative transition-all duration-500 touch-none h-[320px] sm:h-[350px]`}>
              {isMobile && (
                <div 
                  className="absolute inset-0 z-10"
                  onPointerMove={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const percentage = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                    setActiveIndexPerf(Math.round(percentage * (chartData.length - 1)));
                  }}
                  onPointerDown={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const percentage = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                    setActiveIndexPerf(Math.round(percentage * (chartData.length - 1)));
                  }}
                />
              )}
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart 
                  data={chartData} 
                  margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                  onMouseMove={(state) => {
                    if (!isMobile && state && typeof state.activeTooltipIndex === 'number') {
                      setActiveIndexPerf(state.activeTooltipIndex)
                    }
                  }}
                  onMouseLeave={() => !isMobile && setActiveIndexPerf(null)}
                >
                  <defs>
                    <linearGradient id="colorGen" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.1}/>
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorLoad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.1}/>
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke={darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}
                  />
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fontWeight: 700, fill: darkMode ? '#64748b' : '#94a3b8' }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fontWeight: 700, fill: darkMode ? '#64748b' : '#94a3b8' }}
                  />
                  <Tooltip
                    content={<CustomTooltip darkMode={darkMode} unit={isDailyView ? 'kWh/day' : 'kWh'} isMobile={isMobile} />}
                    wrapperStyle={isMobile ? { display: 'none', pointerEvents: 'none' } : undefined}
                    cursor={isMobile ? false : true}
                  />
                  <Legend
                    verticalAlign="top"
                    align="right"
                    iconType="circle"
                    wrapperStyle={{ paddingBottom: '30px', fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}
                  />
                  <Area
                    name="Generation"
                    type="monotone"
                    dataKey={isDailyView ? 'solarGenerationDaily' : 'solarGenerationKwh'}
                    stroke="#f59e0b"
                    fillOpacity={1}
                    fill="url(#colorGen)"
                    strokeWidth={3}
                    isAnimationActive={false}
                    activeDot={{ r: 4, strokeWidth: 0, style: { pointerEvents: 'none' } }}
                    dot={(props: any) => <circle key={`gen-${props.index}`} cx={props.cx} cy={props.cy} r={isMobile && props.index === activeIndexPerf ? 4 : 0} fill="#f59e0b" style={{ opacity: isMobile && props.index === activeIndexPerf ? 1 : 0 }} />}
                  />
                  <Area
                    name="Consumption"
                    type="monotone"
                    dataKey={isDailyView ? 'totalLoadDaily' : 'totalLoadKwh'}
                    stroke="#6366f1"
                    fillOpacity={1}
                    fill="url(#colorLoad)"
                    strokeWidth={3}
                    isAnimationActive={false}
                    activeDot={{ r: 4, strokeWidth: 0, style: { pointerEvents: 'none' } }}
                    dot={(props: any) => <circle key={`con-${props.index}`} cx={props.cx} cy={props.cy} r={isMobile && props.index === activeIndexPerf ? 4 : 0} fill="#6366f1" style={{ opacity: isMobile && props.index === activeIndexPerf ? 1 : 0 }} />}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Bottom KPI Boxes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className={`p-6 rounded-2xl border text-center ${
              darkMode ? 'bg-slate-900/40 border-white/5' : 'bg-white border-slate-200 shadow-sm'
            }`}>
              <div className={`text-3xl font-black tracking-tighter ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {stats.selfConsumption}<span className="text-sm opacity-40 ml-0.5">%</span>
              </div>
              <div className={`flex items-center justify-center gap-1 text-[10px] font-black uppercase tracking-widest mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                <button
                  onClick={() => openHelpToItem('Self-Consumption')}
                  className={`shrink-0 p-0.5 rounded-full transition-colors cursor-pointer ${
                    darkMode ? 'text-slate-600 hover:text-emerald-500' : 'text-slate-400 hover:text-emerald-600'
                  }`}
                >
                  <Info size={10} />
                </button>
                Self Consumption
              </div>
            </div>
            <div className={`p-6 rounded-2xl border text-center ${
              darkMode ? 'bg-slate-900/40 border-white/5' : 'bg-white border-slate-200 shadow-sm'
            }`}>
              <div className={`text-3xl font-black tracking-tighter ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {stats.exportToGrid}<span className="text-sm opacity-40 ml-0.5">%</span>
              </div>
              <div className={`flex items-center justify-center gap-1 text-[10px] font-black uppercase tracking-widest mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                <button
                  onClick={() => openHelpToItem('Export to Grid')}
                  className={`shrink-0 p-0.5 rounded-full transition-colors cursor-pointer ${
                    darkMode ? 'text-slate-600 hover:text-emerald-500' : 'text-slate-400 hover:text-emerald-600'
                  }`}
                >
                  <Info size={10} />
                </button>
                Export to grid
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const CustomTooltip = ({ active, payload, label, darkMode, unit = '$', isMobile }: any) => {
  if (isMobile) return null
  if (active && payload && payload.length) {
    return (
      <div
        className={`p-4 rounded-2xl border shadow-2xl backdrop-blur-lg ${
          darkMode ? 'bg-slate-900/75 border-white/10' : 'bg-white/75 border-slate-200'
        }`}
      >
        <p className={`text-[10px] font-black uppercase tracking-widest mb-3 pb-2 border-b ${darkMode ? 'text-slate-500 border-white/5' : 'text-slate-400 border-slate-100'}`}>
          {label}
        </p>
        <div className="space-y-2">
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center justify-between gap-8">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className={`text-[10px] font-bold uppercase tracking-tight ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {entry.name}
                </span>
              </div>
              <span className={`text-xs font-black ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {unit === '$' 
                  ? `$${Math.round(entry.value).toLocaleString()}` 
                  : entry.name === 'Avg SoC' 
                    ? `${Math.round(entry.value)}%` 
                    : `${Math.round(entry.value).toLocaleString()} ${unit}`}
              </span>
            </div>
          ))}
        </div>
      </div>
    )
  }
  return null
}

